<template>
  <v-card flat color="transparent" class="note-item--tile d-flex flex-column">
    <BulkSelectCheckbox
      v-if="isBulkActionsActive"
      class="checkbox ml-1 mt-1"
      :checked="isSelected"
      @click="toggleSelect"
    />

    <!-- Full-width colored title background -->
    <div
      class="note-title-container rounded mb-2"
      :style="containerStyle"
      @click="onTitleClick"
    >
      <div class="note-title text-body-1 font-weight-semibold text-neutral900">
        {{ note.title || $t('common.untitled_note') }}
      </div>
    </div>

    <div class="text-neutral600 d-flex align-center mb-3 text-caption">
      <NoteSource :note="note" />
      <span class="mx-1">&middot;</span>
      <NoteDate :value="note.createdAt" />
    </div>

    <NoteTags v-if="note.tags?.length" class="mb-3" :tags="note.tags" :max="3" @tag-selected="onTagSelected" />

    <v-spacer />

    <NoteActions
      :key="note.id"
      :note="note"
      :user-id="userId"
      :public-mode="publicMode"
      :membership="membership"
      class="text-text"
      @favorite="$emit('favorite')"
      @save="$emit('save')"
      @add-to-collection="$emit('add-to-collection')"
      @add-to-collection-from-collection="$emit('add-to-collection-from-collection')"
      @comments="$emit('comments')"
      @like="$emit('like')"
      @likes="$emit('likes')"
      @more="$emit('more')"
      @pin-edit="$emit('pin-edit')"
    />
  </v-card>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import NoteSource from './NoteSource.vue'
import NoteActions from './NoteActions.vue'
import NoteDate from './NoteDate.vue'
import NoteTags from './NoteTags.vue'
import { useBulkActionsV2 } from '@/composables/useBulkActionsV2'
import BulkSelectCheckbox from '@/components/BulkSelectCheckbox.vue'
import { useNoteColors } from '@/utils/noteColors'

const props = defineProps({
  note: {
    type: Object,
    required: true,
  },
  userId: {
    type: String,
    default: null,
  },
  membership: {
    type: Object,
    default: null,
  },
  publicMode: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'toggle-select',
  'tag-selected',
  'favorite',
  'save',
  'add-to-collection',
  'add-to-collection-from-collection',
  'comments',
  'like',
  'likes',
  'more',
  'pin-edit',
])

const router = useRouter()
const route = useRoute()
const { isBulkActionsActive, isItemSelected } = useBulkActionsV2()
const { getNoteColor } = useNoteColors()

const isSelected = computed(() => {
  return isItemSelected(props.note.id)
})

const containerStyle = computed(() => {
  return {
    backgroundColor: getNoteColor(props.note.color),
    height: '100%',
  }
})

function onTitleClick() {
  if (isBulkActionsActive.value) {
    toggleSelect()
  } else {
    navigateToDetail()
  }
}

function navigateToDetail() {
  if (props.publicMode) {
    const { username, id } = route.params
    router.push(`/u/${username}/collections/${id}/notes/${props.note.id}`)
    return
  }
  if (props.note.isPrivate) {
    router.push(`/notes/${props.note.id}`)
  } else {
    router.push(`/collections/${props.note.ownerId}/notes/${props.note.id}`)
  }
}

function toggleSelect() {
  emit('toggle-select', props.note)
}

function onTagSelected(tag) {
  emit('tag-selected', tag)
}
</script>

<style lang="scss" scoped>
.note-item--tile {
  height: 100%;

  .checkbox {
    position: absolute;
    left: 0;
    top: 0;
    z-index: 1;
  }
}

.note-title-container {
  width: 100%;
  padding: 12px;
  cursor: pointer;
}

.note-title {
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  display: -webkit-box;
  overflow: hidden;
  word-break: break-word;
  line-height: 1.4 !important;
}
</style>
